import { MutableRefObject } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives";

import {
    Adg4Tabs,
    Adg4DraggableTab,
    Adg4DraggableTabList
} from "@/components/adg4/tabs/src";
import { Picture } from "@/components/wpl/image/src";
import { wacToken } from "@/libraries/design/foundations";
import { Row } from "@/libraries/wpl/foundations/src";

import {
    ActiveTeamDataProps,
    // @ts-ignore
    HeroJiraCTAProps
} from "../hero-jira-cta-template";

import { ActiveMedia } from "./active-media";
import { TEAM_DATA } from "./team-data";

const tabsStyle = css({
    display: "flex",
    justifyContent: "space-between",
    margin: "0 auto",
    marginBlockEnd: "50px"
});
const buttonResetStyle = css({
    "border": "none",
    "background": "transparent",
    "padding": "0",

    ":hover": {
        cursor: "pointer"
    }
});
const teamCardStyles = css({
    "boxSizing": "border-box",
    "display": "flex",
    "flexDirection": "column",
    "width": "173px",
    "height": "90px",
    "alignItems": "center",
    "padding": "8px",

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".hover-icon": {
        display: "none"
    },

    ":hover": {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".default-icon": {
            display: "none"
        },

        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".hover-icon": {
            display: "block"
        }
    }
});
const teamCardActiveStyles = css({
    border: `1px solid ${wacToken("color.primary.black")}`,
    borderRadius: "6px",

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    span: {
        fontWeight: "700"
    }
});
const iconContainerStyles = css({
    height: "48px",
    width: "48px"
});
const teamCardTextStyles = css({
    fontFamily: wacToken("font.charlie.display"),
    fontWeight: 400,
    fontSize: "1rem",
    lineHeight: "1.5rem",
    color: wacToken("color.primary.black")
});
const videoContainerStyles = css({
    "@keyframes fadein": {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        from: {
            opacity: 0.7
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        to: {
            opacity: 1
        }
    },

    "width": "100%",
    "aspectRatio": "16/9",
    "position": "relative",
    "marginBlockEnd": "8%"
});
const backgroundStyles = css({
    position: "absolute",
    insetInlineStart: "-100px",
    insetBlockStart: "58%",
    width: "calc(100% + 200px)",
    height: "55%",
    backgroundColor: wacToken("color.primary.blue"),
    zIndex: "-1",
    clipPath:
        "polygon(0.5% 0, 50% 0, 50% 10%, 98.5% 0%, 100% 85%, 17.75% 100%, 17.25% 85%, 0 85%)",

    [media.below.sm]: {
        insetInlineStart: "-50px"
    }
});

type TabsProps = {
    tabsWrapperRef: MutableRefObject<HTMLDivElement | null>;
};

export const Tabs = ({
    heroSelectedTeamIndex,
    onSelectHeroTab,
    tabsWrapperRef,
    ...activeTeamData
}: HeroJiraCTAProps & ActiveTeamDataProps & TabsProps) => {
    const intl = useIntl();

    return (
        <>
            <div ref={tabsWrapperRef}>
                <Adg4Tabs>
                    <Adg4DraggableTabList css={[tabsStyle]}>
                        {TEAM_DATA.map(
                            (
                                { title, id, icon, iconHover, englishLabel },
                                index
                            ) => {
                                return (
                                    <Adg4DraggableTab
                                        key={id}
                                        onClick={() => {
                                            onSelectHeroTab(index);
                                        }}
                                    >
                                        <button
                                            css={buttonResetStyle}
                                            data-product-key="home"
                                            data-event-component="button"
                                            data-event-container="heroSection"
                                            data-event="clicked"
                                            data-label-english={englishLabel}
                                            aria-label={intl.formatMessage(
                                                title
                                            )}
                                        >
                                            <div
                                                css={[
                                                    teamCardStyles,
                                                    heroSelectedTeamIndex ===
                                                        index &&
                                                        teamCardActiveStyles
                                                ]}
                                            >
                                                <div
                                                    css={[iconContainerStyles]}
                                                    className="default-icon"
                                                >
                                                    {
                                                        <Picture
                                                            image={{
                                                                src: `${icon}`,
                                                                alt: "",
                                                                draggable:
                                                                    "false",
                                                                width: 48,
                                                                height: 48
                                                            }}
                                                            forceEagerLoad={
                                                                true
                                                            }
                                                        />
                                                    }
                                                </div>
                                                <div
                                                    css={[iconContainerStyles]}
                                                    className="hover-icon"
                                                >
                                                    <Picture
                                                        image={{
                                                            src: `${
                                                                heroSelectedTeamIndex !==
                                                                index
                                                                    ? iconHover
                                                                    : icon
                                                            }`,
                                                            alt: "",
                                                            draggable: "false",
                                                            width: 48,
                                                            height: 48,
                                                            loading: "eager",
                                                            fetchpriority: "low"
                                                        }}
                                                    />
                                                </div>
                                                <span css={teamCardTextStyles}>
                                                    {intl.formatMessage(title)}
                                                </span>
                                            </div>
                                        </button>
                                    </Adg4DraggableTab>
                                );
                            }
                        )}
                    </Adg4DraggableTabList>
                </Adg4Tabs>
            </div>
            <Row layout="10">
                <div>
                    <div css={videoContainerStyles}>
                        <ActiveMedia {...activeTeamData} />
                        <div css={backgroundStyles} />
                    </div>
                </div>
            </Row>
        </>
    );
};
